import React, { useEffect, useRef, useState, useMemo } from 'react';
import { AnimatePresence, motion } from "motion/react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { useAppStore } from '../stores/useAppStore';
import Button from './Button';
import IconSearch from './IconSearch';
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import AnalyticsWrapper from './AnalyticsWrapper';
import useMenuNavigation from '../hooks/useMenuNavigation';
import { CAROUSEL_TYPES } from '../constants/carouselTypes';

export default function Search() {
    const { setSearchIsOpen, content } = useAppStore();
    const { openCarousel } = useAppStore((state) => state.carousel);
    const [input, setInput] = useState("");
    const [layout, setLayout] = useState("default");
    const keyboard = useRef();
    const navigateTo = useMenuNavigation();

    // Construir el índice de búsqueda dinámicamente desde sections
    const searchIndex = useMemo(() => {
        if (!content?.sections) return [];
        
        const index = [];
        
        content.sections.forEach(section => {
            if (!section.searchable || !section.searchConfig) return;
            
            const { dataKey, searchFields, carouselType } = section.searchConfig;
            let dataSource = content[dataKey];
            
            if (!dataSource) return;
            
            // Si el dataSource tiene una propiedad 'items', usar esa en lugar del objeto completo
            if (dataSource.items && Array.isArray(dataSource.items)) {
                dataSource = dataSource.items;
            }
            
            // Convertir a array si no lo es (para manejar objetos y arrays)
            const items = Array.isArray(dataSource) ? dataSource : [dataSource];
            
            items.forEach((item, itemIndex) => {
                // Construir el texto de búsqueda concatenando todos los campos especificados
                const searchText = searchFields.map(field => {
                    const value = item[field];
                    if (Array.isArray(value)) {
                        return value.join(' ');
                    }
                    return value || '';
                }).join(' ').toLowerCase();
                
                index.push({
                    // Datos del item
                    ...item,
                    // Metadata para la navegación
                    _meta: {
                        menuKey: section.key,
                        dataKey: dataKey,
                        carouselType: carouselType,
                        itemIndex: itemIndex,
                        searchText: searchText,
                        sectionTitle: section.title
                    }
                });
            });
        });
        
        return index;
    }, [content]);

    const filteredResults = useMemo(() => {
        if (input.length < 2) return [];
        
        const searchTerm = input.toLowerCase().trim();
        
        return searchIndex.filter(item => 
            item._meta.searchText.includes(searchTerm)
        );
    }, [input, searchIndex]);

    const noResults = filteredResults.length === 0;
    
    const variants = {
        closed: {
            backgroundColor: '#1a2951',
            clipPath: 'circle(4.875rem at calc(100% - 11.5rem) 11.5rem)',
            transition: {
                duration: 0.5,
                ease: "easeIn",
                delay: 0.1
            },
        },
        open: {
            backgroundColor: '#061933',
            clipPath: 'circle(140% at calc(100% - 11.5rem) 11.5rem)',
            transition: {
                duration: 0.7,
                ease: "easeOut",
                delay: 0.2
            },
        },
    };

    const keyboardVariants = {
        closed: { y: "100%", transition: { duration: 0.4, ease: "easeIn" } },
        open: { y: 0, transition: { duration: 0.5, ease: "easeOut", delay: 0.6 } }
    };
    
    const onChange = input => {
        setInput(input);
    };

    const onChangeInput = (event) => {
        const value = event.target.value;
        setInput(value); // Actualizar el estado con el valor del input físico
        if (keyboard.current) {
            keyboard.current.setInput(value); // Sincronizar con el teclado virtual
        }
    };
    
    const onKeyPress = (button) => {
        if (button === "{shift}" || button === "{lock}") handleShift(); // Manejar el botón shift/caps lock
    };
    
    const handleShift = () => {
        const newLayoutName = layout === "default" ? "shift" : "default";
        setLayout(newLayoutName);
    };

    const handleResultClick = (result) => {
        const { menuKey, carouselType, itemIndex } = result._meta;
        
        setSearchIsOpen(false);  // Cierra el buscador
        
        // Navega a la ruta correspondiente
        navigateTo(menuKey);
        
        // Si tiene carouselType, abre el carousel después de navegar
        if (carouselType) {
            // Pequeño delay para asegurar que la navegación se complete primero
            setTimeout(() => {
                openCarousel(itemIndex, CAROUSEL_TYPES[carouselType]);
            }, 10);
        }
    };

    useEffect(()=> {
        AnalyticsWrapper.trackPageView(`/search`);
    }, []);
    
    return (
        
        <motion.div
            className='h-full w-full bg-navy fixed z-50 transform-gpu will-change-clip-path'
            variants={variants}
            initial="closed"
            animate="open"
            exit="closed"
        >
            <motion.div className={`
                relative
                w-[129.375rem] h-[12.75rem] mx-auto overflow-visible`}
                initial={{ marginTop: window.innerHeight / 2 - 51 }}
                animate={{ marginTop: noResults ? (window.innerHeight / 2) - 51 : 210 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}>
                <span className="
                    absolute
                    w-full
                    -top-[13rem]
                    font-extralight
                    text-[7.5rem]
                    text-center
                    leading-none">What are you looking for?</span>
                <div className="
                        absolute
                        bg-navy
                        flex
                        items-center
                        justify-center
                        rounded-full
                        -z-10
                        bg-gradient-animated
                        blur-md
                        w-full
                        h-full
                        "></div>
                <div className="
                    absolute
                    bg-navy
                    flex
                    items-center
                    justify-center
                    rounded-full
                    -z-10
                    bg-gradient-animated
                    w-full
                    h-full
                    "></div>
                <Button
                    onClick={null}
                    className="absolute top-[1.5rem] right-[1.5rem] w-[9.75rem] h-[9.75rem] justify-center pointer-events-none">
                    <IconSearch className="w-[3.5rem] h-auto" />
                </Button>
                <input
                    disabled
                    type="text"
                    placeholder="Start searching..."
                    value={input}
                    onChange={onChangeInput}
                    className="
                        ml-[0.25rem]
                        mt-[0.25rem]
                        w-[calc(100%-0.5rem)]
                        h-[calc(100%-0.5rem)]
                        pl-[6rem] pr-[13.125rem]
                        rounded-full outline-none bg-navy text-white text-[3.375rem] font-extralight"
                />
                
                {/* Resultados */}
                <AnimatePresence>
                {input.length >= 2 && (
                    <motion.div
                    className="-z-20 -mt-[6rem] relative border-[0.25rem] border-[#e2e2e2] rounded-b-[4rem]"
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }} // Para evitar que se vea el contenido mientras se anima la altura
                    >
                    <ScrollArea.Root 
                        type={filteredResults.length > 2 ? "always" : "auto"}
                        className="rounded-md shadow-md max-h-[58rem] overflow-hidden w-full"
                    >
                        <ScrollArea.Viewport 
                        className="p-[4rem] pt-[10rem] max-h-[58rem] overflow-y-auto touch-auto"
                        >
                        <AnimatePresence>
                            { !noResults ? (
                            filteredResults.map((result, index) => {
                                const isLast = index === filteredResults.length - 1;
                                
                                // Obtener el título del resultado dependiendo del tipo de dato
                                const displayTitle = result.title || result.summary || result.header || 'Untitled';
                                const displayId = result.id || (index + 1);
                                
                                // Construir la línea de información: Nombre del apartado | Categoría (si existe)
                                const sectionName = result._meta.sectionTitle || '';
                                const category = result.category || '';
                                const displayInfo = category 
                                    ? `${sectionName} | ${category}` 
                                    : sectionName;
                                
                                return (
                                    <motion.div
                                        key={`${result._meta.menuKey}-${result._meta.itemIndex}`}
                                        initial={{opacity: 0}}
                                        animate={{opacity: 1}}
                                        transition={{ duration: 0.3, delay: 0.1 * index }}
                                        onClick={() => handleResultClick(result)}
                                        className={`click-effect flex p-[5rem] gap-[3.125rem] rounded-[2rem] bg-[#061933] border-silver border-[0.25rem] ${isLast ? '' : 'mb-[1.875rem]'} items-center`}
                                    >
                                        {/*
                                        <span className={`flex flex-0 items-center justify-center rounded-full w-[6.625rem] h-[6.625rem] bg-[#03BCE9] font-light text-[3rem]`}>
                                            {displayId}
                                        </span>
                                        */}
                                        <div className='flex-1 font-extralight'>
                                            <p className="text-[3.375rem] mb-[1rem] leading-none">{displayTitle}</p>
                                            <p className="text-[2.5rem] leading-[3rem]">{displayInfo}</p>
                                        </div>
                                    </motion.div>
                                );
                            })
                            ) : (
                                <p className="text-[3.375rem] leading-none text-center">No results</p>
                            )}
                        </AnimatePresence>
                        </ScrollArea.Viewport>
                        <ScrollArea.Scrollbar 
                        orientation="vertical" 
                        className="w-6 bg-gray-200 rounded-full"
                        style={{ top: '10rem', bottom: '4rem' }}  // Hace que la barra respete el padding superior/inferior
                        >
                        <ScrollArea.Thumb className="bg-gray-400 rounded-full" />
                        </ScrollArea.Scrollbar>
                    </ScrollArea.Root>
                    </motion.div>
                )}
                </AnimatePresence>



            </motion.div>

            <motion.div
                initial="closed"
                animate="open"
                exit="closed"
                variants={keyboardVariants}
                className="fixed bottom-[5.75rem] left-1/2"
            >
                <Keyboard
                    keyboardRef={r => (keyboard.current = r)}
                    layoutName={layout}
                    onChange={onChange}
                    onKeyPress={onKeyPress}
                    layout={{
                        default: [
                          "1 2 3 4 5 6 7 8 9 0 {bksp}",
                          "Q W E R T Y U I O P",
                          'A S D F G H J K L',
                          "Z X C V B N M",
                          "{space}"
                        ]
                      }}
                />
            </motion.div>

        </motion.div>
    );
}